import type { AnimationDefsByCompId, TriggerType, ViewportManager } from './types'
import { MotionEffectsManager } from './MotionEffectsManager'

export default function init({
	manager,
	animationDefsByCompId,
}: {
	manager: MotionEffectsManager
	animationDefsByCompId: AnimationDefsByCompId
}): ViewportManager {
	const INTERSECTION_THRESHOLD = 0
	const EXIT_ROOT_MARGIN = '50%'
	const ENTRY_ROOT_MARGIN = '10%'

	const observers: Record<string, Record<'entry' | 'exit', IntersectionObserver>> = {}

	/**
	 * build an intersection observer
	 * @param callback
	 * @param threshold
	 * @param rootMarginInPixels
	 * @returns {IntersectionObserver}
	 */
	function getObserver(
		callback: IntersectionObserverCallback,
		threshold: number,
		rootMarginInPixels: string = '0px'
	) {
		const options = {
			root: null, // document
			rootMargin: rootMarginInPixels,
			threshold: [threshold],
		}

		return new window.IntersectionObserver(callback, options)
	}

	const getEffectIdsFromTargetId = (targetId: string) => {
		return Object.keys(animationDefsByCompId[targetId])
	}

	/**
	 * trigger animation when element is it NOT visible
	 * @param entries
	 */
	function intersectionExitHandler(entries: Array<IntersectionObserverEntry>) {
		const idsToTrigger: Array<TriggerType> = entries
			.filter((entry) => !entry.isIntersecting)
			.flatMap((entry) => {
				const animations = getEffectIdsFromTargetId(entry.target.id)

				return animations.map((effectId) => ({
					effectId,
					action: 'hold',
				}))
			})

		manager.trigger(idsToTrigger)
	}

	/**
	 * trigger animation when element is visible
	 * @param entries
	 */
	function intersectionEntryHandler(entries: Array<IntersectionObserverEntry>) {
		const idsToTrigger: Array<TriggerType> = entries
			.filter((entry) => entry.isIntersecting)
			.flatMap((entry) => {
				const animations = getEffectIdsFromTargetId(entry.target.id)

				return animations.map((effectId) => ({
					effectId,
					action: 'resume',
				}))
			})

		manager.trigger(idsToTrigger)
	}

	/**
	 * Observe elements and save to observers array
	 * @param el
	 * @param threshold
	 * @param pageId
	 */
	function observeElement(el: HTMLElement, pageId: string, threshold: number = INTERSECTION_THRESHOLD) {
		if (!observers[pageId]) {
			observers[pageId] = {
				entry: getObserver(intersectionEntryHandler, threshold, ENTRY_ROOT_MARGIN),
				exit: getObserver(intersectionExitHandler, threshold, EXIT_ROOT_MARGIN),
			}
		}
		observers[pageId].entry.observe(el)
		observers[pageId].exit.observe(el)
	}

	/**
	 * Unobserve all elements for a specific page
	 *  @param pageId
	 */
	function disconnectObserversOnNavigation(pageId: string) {
		observers[pageId]?.entry?.disconnect()
		observers[pageId]?.exit?.disconnect()
	}

	return {
		observe: observeElement,
		disconnect: disconnectObserversOnNavigation,
	}
}
